import { useHistory, useParams } from "react-router";
import { ChangeEvent, useEffect, useLayoutEffect, useState } from 'react'
import { useSession } from 'hooks/useSession'
import { v4 as uuid } from 'uuid';
import { checkChangeDeep } from 'utils/checkChange'
import { isValidatedInput, ValueInputTypes } from 'utils/validateInput'
import { SelectChangeEvent } from 'components/Select'
import { useAlert } from 'hooks/useAlert'
import { useToast } from 'hooks/useToast'
import { BaseRoutes } from 'routes/BaseRoutes'
import { useAuth } from "hooks/useAuth";

interface ParamsProps {
  id: string;
}

interface PermissionProps {
  roleId: number,
  role?: string,
  applicationId: number,
  application?: string
}

interface UserProps {
  userId: number,
  userName?: string,
  email?: string,
  tGroupCompanyId: string,
  tGroupCompany?: string,
  editable: boolean,
  roles?: PermissionProps[] 
}

interface InputInterface {
  value: string,
  error: boolean,
}

interface SelectInterface {
  value: string,
  description: string,
  error: boolean,
}

interface SelectOption {
  value: string,
  description: string,
}

interface InputsValues {
  userName: InputInterface,
  email: InputInterface,
  tGroupCompany: SelectInterface,
  permissions: {
    [id: string]: {
      role: SelectInterface,
      application: SelectInterface,
      disabled: boolean,
    }
  }
}

interface TgroupCompany {
  tGroupCompanyId: number,
  tGroupCompany?: string,
}

interface Application {
  applicationId: number,
  applicationDescription?: string,
}

interface Role {
  roleId: number,
  roleName?: string,
}

const initUser:UserProps = {
  userId: 0,
  userName: '',
  email: '',
  tGroupCompanyId: '',
  tGroupCompany: '',
  editable: true,
  roles: [],
}

const initInputs:InputsValues = {
  userName: {value: '', error: false},
  email: {value: '', error: false},
  tGroupCompany: {value: '', description: '', error: false},
  permissions: {
    [uuid()]: {
      role: {
        value: '',
        description: '',
        error: false
      },
      application: {
        value: '',
        description: '',
        error: false
      },
      disabled: false,
    },
  }
}

const changeNameTag = (props: string) => (
  {
    "userName": "nome completo",
    "email": "e-mail",
    "tGroupCompany": "empresa",
    "permissions": "permissões"
  }
)[props];

export const useUserDetails = () => {
  const [user, setUser] = useState<UserProps>(initUser);
  const [initInputsValues, setInitInputsValues] = useState<InputsValues>(initInputs);
  const [inputsValues, setInputsValues] = useState<InputsValues>(initInputs);
  const [changeFooter, setChangeFooter] = useState<string[]>([])
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [validForm, setValidForm] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingUnmask, setLoadingUnmask] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>();
  const [applicationOptions, setApplicationOptions] = useState<SelectOption[]>();
  const [initApplicationOptions, setInitApplicationOptions] = useState<SelectOption[]>();
  const history = useHistory();
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>();
  const [currentPage, setCurrentPage] = useState(history.location.pathname);
  const {getSessionAuthorization, signOut, getUser} = useSession();
  const { openAlert, closeAlert, alert, setTextAlertInterface, caller } = useAlert();
  const { id } = useParams<ParamsProps>();
  const {setToast} = useToast();
  const { connectApi, connectAllApi } = useAuth();

  const push = (route: string): void => {
    history.push(route);
  }
  /* HOOKS */
  useEffect(()=>{
    getUser();
    getPermissions();
    setCurrentPage(history.location.pathname)
  }, [id])

  useEffect(()=>{
    let permissionsAux:{[id: string]: any} = {};
    user?.roles?.forEach((permission) => {
      permissionsAux[uuid()] = {
        role: {
          value: permission.roleId,
          description: permission.role,
          error: false,
        },
        application: {
          value: permission.applicationId,
          description: permission.application,
          error: false,
        },
        disabled: true,
      }
    })
    const inputs:InputsValues = {
      userName: {value: user?.userName ?? '', error: false},
      email: {value: user?.email ?? '', error: false},
      tGroupCompany: {value: user?.tGroupCompanyId ?? '', description: user?.tGroupCompany ?? '', error: false},
      permissions: permissionsAux,
    }
    setInitInputsValues(inputs);
    setInputsValues(inputs);

    if (user?.editable) {
      getSelectOptions();
    }
  }, [user])

  useEffect(() => {
    const changed = checkChangeDeep(inputsValues, initInputsValues);
    const translatedChange = changed.map((change) => {
      return changeNameTag(change) ?? '';
    })
    setChangeFooter(translatedChange)
  }, [inputsValues])

  useEffect(() => {
    if (caller && caller?.match("removePermission")) {
      if (alert.typeContinue === "save") {
        const roleId = caller.split(':')[1];
        const applicationId = caller.split(':')[2];
        removePermissionHandler(roleId, applicationId);
      }
      alert.typeContinue === "discard" && closeAlert();
    } else {
      alert.typeContinue === "save" && submitAlertHandler();
      alert.typeContinue === "discard" && discartAlertHandler();
    }
  }, [alert])

  useEffect(() => {
    setValidForm(formValid());
  }, [inputsValues]);

  useEffect(() => {
    reduceApplicationOptions();
    if (Object.keys(inputsValues.permissions).length == 0)
      addPermission();
  }, [inputsValues.permissions]);

  useEffect(() => {
    if (initApplicationOptions) {
      reduceApplicationOptions();
    }
  }, [initApplicationOptions]);

  /* LOCAL METHODS */
  const reduceApplicationOptions = () => {
    let selectedApplications:SelectOption[] = [];
    let auxApplications = [...(initApplicationOptions ?? [])];

    for (let [key, value] of Object.entries(inputsValues.permissions))   {
      let application = value.application;
      if (application.value)
        selectedApplications.push({value: application.value.toString(), description: application.description});
    }

    for (let i = 0; i < selectedApplications.length; ++ i) {
      let application = selectedApplications[i];
      let option = auxApplications.find((a) => a.value === application.value);
      if (option) {
        const index = auxApplications.indexOf(option, 0);
        if (index > -1) {
          auxApplications.splice(index, 1);
        }
      }
    }
    setApplicationOptions(auxApplications);
  }

  const formValid = ():boolean => {
    // essa logica ta horrorosa mas eh o que deu por enquanto
    if (inputsValues.userName.value.length == 0) {
      return false;
    }
    if (
      isValidatedInput([{
        name: 'email',
        valueInput: inputsValues.email.value,
        validateOptions: ['email'],
      }]).hasError
    ) {
      return false;
    }
    if (inputsValues.tGroupCompany.value.length == 0) {
      return false;
    }
    for (let [key, value] of Object.entries(inputsValues.permissions)) {
      if (value.application.value.length == 0 || value.role.value.length == 0 ) {
        return false;
      }
    }
    return true;
  }
  /* EVENT HANDLERS */
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const field = event.target.name;
    const value = event.target.value;

    const { validatedObject, hasError } = isValidatedInput([{
      name: field,
      valueInput: value,
      validateOptions: [field as ValueInputTypes, 'minCharacter'],
      min: 1,
    }])

    setInputsValues({
      ...inputsValues,
      [field]: {
        ...inputsValues[field as keyof typeof inputsValues],
        value: value,
        error: hasError},
    })
  }

  const handleCompanySelectChange = (event: SelectChangeEvent) => {
    const targetId = event.valueId;
    const description = event.optionSelected;

    setInputsValues({
      ...inputsValues,
      tGroupCompany: {
        value: targetId ?? '',
        description: description ?? '',
        error: false
      }
    })
  }

  const handlePermissionSelectChange = (event: SelectChangeEvent) => {
    const [target, index] = event.dataId?.split(':') ?? [];
    let targetId = event.valueId;
    let description = event.optionSelected;
    setInputsValues({
      ...inputsValues,
      permissions: {
        ...inputsValues.permissions,
        [index]: {
          ...inputsValues.permissions[index],
          [target]: {
            ...inputsValues[target as keyof typeof inputsValues],
            value: targetId,
            description: description,
            error: false
          }
        }
      }
    })
  }

  const addPermission = ():void => {
    let newPermission = {
      role: {value: '', description: '', error: false},
      application: {value: '', description: '', error: false},
      disabled: false,
    };

    setInputsValues({
      ...inputsValues,
      permissions: {
        ...inputsValues.permissions,
        [uuid()]: newPermission
      }
    });
  }

  const handleRemovePermission = (permissionId: string): void => {
    let selectedPermission = inputsValues.permissions[permissionId];
    if (selectedPermission.disabled) {
      setTextAlertInterface({
        title: 'Remover permissão',
        description: 'Essa ação removerá o privilegio deste usuário',
        closeAlertText: "Voltar",
        continueAlertText: "Sim",
        positionCenter: true,
      });
      openAlert(true, `removePermission:${selectedPermission.role.value}:${selectedPermission.application.value}`);
    } else {
      let auxPermissions = {...inputsValues.permissions};
      delete auxPermissions[permissionId];
      setInputsValues({
        ...inputsValues,
        permissions: auxPermissions,
      });
    }
  }

  const discartAlertHandler = () => {
    setInputsValues(initInputsValues);
    setChangeFooter([]);
    closeAlert();
  }

  const submitAlertHandler = () => {
    updateUser();
  }

  const discardHandler = () => {
    openAlert(false);
  }

  const submitHandler = () => {
    openAlert(true);
  }

  const logOff = () => {
    signOut();
    history.push("/")
  }

  const getEmailUnmask = async () => {
    setLoadingUnmask(true)
    const user = getUser();
    const idLocal = user && history.location.pathname === "/account" ? user.id : id;
    connectApi({type: 'get', url: `users/${idLocal}/unmask?field=email`}).then((response) => {
      const data = response.data;
      setInitInputsValues({...inputsValues, email: {
        ...inputsValues.email, 
        value: data.email
      }})
      setInputsValues({...inputsValues, email: {
        ...inputsValues.email, 
        value: data.email
      }})
  
      setIsEditable(true);
      setHasError(false);
      setLoadingUnmask(false)
    }).catch((_) => setHasError(true));
  }

  /* API CALLS */
  const getPermissions = async() => {

    // Esse controlador eh compartilhado entre a tela de permissoes e minha conta
    // por isso preciso verificar se existe um id externo (tela de permissoes)
    // se nao existir um id externo significa que estou na tela da minha conta, entao uso o id do getUser()
    const user = getUser();
    connectApi({type: 'get', url: `users/${id ?? user?.id}`}).then((response) => {
      const responseUser:UserProps = response.data;
      setUser(responseUser);
      setHasError(false);
    }).catch((_) => {
      setHasError(true);
    }).finally(() => {
      setLoading(false);
    })
  }

  const updateUser = async() => {
    const userToken = getUser();
    const idLocal = userToken && history.location.pathname === "/account" ? userToken.id : id;

    var localUser:UserProps = {
      userId: user.userId,
      userName: inputsValues.userName.value,
      tGroupCompanyId: inputsValues.tGroupCompany.value,
      editable: user.editable,
      roles: Object.keys(inputsValues.permissions).map((key) => {
        return {
          roleId: parseInt(inputsValues.permissions[key].role.value),
          applicationId: parseInt(inputsValues.permissions[key].application.value),
        }
      })
    }

    if (isEditable) {
      localUser.email = inputsValues.email.value;
    }

    setSubmitting(true);
    connectApi({type: 'put', url: `users/${idLocal}`, body: localUser}).then((response) => {
      setToast({
        message: `Usuário atualizado com sucesso`,
        isActive: true,
        type: 'success',
        timeToRemoveToast: 3000
      });
      push(BaseRoutes.permissions)
    }).catch((error) => {
      const errorResponse = error.response.data;
      setToast({
        message: `Erro ${errorResponse.StatusCode}: ${errorResponse.Message}`,
        isActive: true,
        type: 'error',
        timeToRemoveToast: 3000
      })
    }).finally(() => {
      closeAlert();
      setSubmitting(false);
    })

  }

  const getSelectOptions = async () => {

    setLoading(true);
    connectAllApi([
      {type: 'get', url: `users/tgroup-companies`},
      {type: 'get', url: `users/applications`},
      {type: 'get', url: `users/roles`},
    ]).then((responses) => {
      if (responses[0].status !== "rejected") {
        let body = responses[0].value.data;
        setHasError(false);
        setCompanyOptions(
          body.map((item: TgroupCompany):SelectOption => ({
            value: item.tGroupCompanyId.toString(),
            description: item.tGroupCompany ?? 'N/A',
          })),
        );
      } else {
        setHasError(true);
        return;
      }
  
      if (responses[1].status !== "rejected") {
        let body = responses[1].value.data;
        let options = body.map((item: Application):SelectOption => ({
          value: item.applicationId.toString(),
          description: item.applicationDescription ?? 'N/A',
        }));
        setHasError(false);
        setInitApplicationOptions(options);
        setApplicationOptions(options);
      } else {
        setHasError(true);
        return;
      }
  
      if (responses[2].status !== "rejected") {
        let body = responses[2].value.data;
        setHasError(false);
        setRoleOptions(
          body.map((item: Role):SelectOption => ({
            value: item.roleId.toString(),
            description: item.roleName ?? 'N/A',
          })),
        );
      } else {
        setHasError(true);
        return;
      }
    })
    .catch((_) => setHasError(true))
    .finally(() => setLoading(false));
  }

  const removePermissionHandler = (roleId: string, applicationId: string) => {
    connectApi({
      type: 'delete',
      url: `users/${id}/roles/${roleId}?applicationId=${applicationId}`,
      successMessage: 'Permissão removida'
    })
    .then((_) => getPermissions())
    .finally(() => closeAlert());
  }

  useLayoutEffect(() => {}, [])
  
  return {
    loading,
    hasError,
    inputsValues,
    companyOptions,
    applicationOptions,
    roleOptions,
    editable: user?.editable,
    changeFooter,
    validForm,
    handleInputChange,
    handleCompanySelectChange,
    handlePermissionSelectChange,
    addPermission,
    handleRemovePermission,
    discardHandler,
    submitHandler,
    logOff,
    currentPage,
    getEmailUnmask,
    loadingUnmask,
    isEditable
  }
}